import { get } from "lodash";
import { store } from "../../../store";
import { formActions } from "../../../store/formSlice";
import { useAppSelector } from "../../../store/hooks";
import { shallowEqual } from "react-redux";
import { useCpFormContext } from "../../../components/form/useCpFormContext";


export function formSetValue(id: string, path: string, value: any) {
  store.dispatch(formActions.setValue({ id: id, path: path, value: value }));
}

export function formTriggerValidation(id: string) {
  store.dispatch(formActions.triggerValidation(id));
}

export function formSet(id: string, path: string, value: any) {
  store.dispatch(formActions.set({ id: id, path: path, value: value }));
}

export async function formInitialize(id: string, values: any, validationFn?: any) {
  store.dispatch(formActions.setInitialValues({ id: id, values: values }));
  if (validationFn) {
    await validationFn(values);
  }
}

export function formUninitialize(id: string) {
  store.dispatch(formActions.clear(id));
}

export function useCpFormSubmit<T>({
  id,
  validationFn,
  onSubmit,
  scroller,
}: {
  id: string;
  validationFn?: (values: T) => Promise<boolean | null>;
  onSubmit: (values: T) => Promise<void>;
  scroller?: (args: { errors: any; warnings: any }) => void;
}) {
  return async function handleSubmit() {
    let form = store.getState().form.instances[id];
    const values: T = form.values as T;
    formSet(id, `meta.submitted`, true);
    try {
      if (validationFn) {
        const hasErrors = await validationFn(values);
        if (hasErrors === null) {
          return;
        }
        if (hasErrors) {
          if (scroller) {
            form = store.getState().form.instances[id];
            const errors = form.errors;
            const warnings = form.warnings;
            scroller({ errors, warnings });
          }
          return;
        }
      }
      formSet(id, `meta.submitting`, true);
      await onSubmit(values);
      const formAfterSave = store.getState().form.instances[id];
      if (formAfterSave?.meta?.applied) {
        store.dispatch(formActions.confirmSave(id));
      }
      formSet(id, `meta.submitting`, false);
    } catch (e) {
      formSet(id, `meta.submitError`, e.message);
    }
  };
}

export function useCpFormAnyValue(name: string) {
  const { id } = useCpFormContext();
  const value = useAppSelector((state) => get(state, `form.instances.${id}.${name}`));
  return value;
}

export function useCpFormAnyValueId(id: string, name: string) {
  const value = useAppSelector((state) => get(state, `form.instances.${id}.${name}`));
  return value;
}

export function useCpFormValue(name: string) {
  const { id } = useCpFormContext();
  const value = useAppSelector((state) => get(state, `form.instances.${id}.values.${name}`));
  return value;
}

export function useCpFormInitialValue(name: string) {
  const { id } = useCpFormContext();
  const value = useAppSelector((state) => get(state, `form.instances.${id}.initialValues.${name}`));
  return value;
}

export function useCpFormModified(name: string): boolean {
  const { id } = useCpFormContext();
  const value: boolean =
    useAppSelector((state) => get(state, `form.instances.${id}.fieldStates.${name}.modified`)) || false;
  return value;
}

export function useCpFormFocused(name: string): boolean {
  const { id } = useCpFormContext();
  const value: boolean =
    useAppSelector((state) => get(state, `form.instances.${id}.fieldStates.${name}.focused`)) || false;
  return value;
}

export function useCpFormVisited(name: string): boolean {
  const { id } = useCpFormContext();
  const value: boolean =
    useAppSelector((state) => get(state, `form.instances.${id}.fieldStates.${name}.visited`)) || false;
  return value;
}

export function useCpFormTouched(name: string): boolean {
  const { id } = useCpFormContext();
  const value: boolean =
    useAppSelector((state) => get(state, `form.instances.${id}.fieldStates.${name}.touched`)) || false;
  return value;
}

export function useCpFormSimulateTouched(name: string): boolean {
  const { id } = useCpFormContext();
  const value: boolean =
    useAppSelector((state) => get(state, `form.instances.${id}.fieldStates.${name}.simulateTouched`)) || false;
  return value;
}

export function useCpFormError(name: string): string {
  const { id } = useCpFormContext();
  const value: string = useAppSelector((state) => get(state, `form.instances.${id}.errors.${name}.message`)) || "";
  return value;
}

export function useCpFormWarning(name: string): string {
  const { id } = useCpFormContext();
  const value: string = useAppSelector((state) => get(state, `form.instances.${id}.warnings.${name}.message`)) || "";
  return value;
}

// Helper to find the first message in a nested object
function findFirstMessage(obj: any): string | null {
  if (!obj || typeof obj !== "object") return null;

  // Check if this object has a message property directly
  if (typeof obj.message === "string") {
    return obj.message;
  }

  // Recursively search nested objects
  for (const key of Object.keys(obj)) {
    const found = findFirstMessage(obj[key]);
    if (found) return found;
  }

  return null;
}

export function useCpFormErrorWarning(names: string[]): { error: string; warning: string } {
  const { id } = useCpFormContext();
  return useAppSelector((state) => {
    const res: { error: string; warning: string } = { error: "", warning: "" };

    const errors = state.form.instances[id]?.errors || {};
    const warnings = state.form.instances[id]?.warnings || {};

    for (const name of names) {
      if (hasNestedPath(errors, name)) {
        // Try to get the actual error message from nested structure
        const errorObj = get(errors, name);
        res.error = findFirstMessage(errorObj) || "Has error";
        continue;
      }

      if (hasNestedPath(warnings, name)) {
        // Try to get the actual warning message from nested structure
        const warningObj = get(warnings, name);
        res.warning = findFirstMessage(warningObj) || "Has warning";
      }
    }

    return res;
  }, shallowEqual);
}

export function useCpFormDirty(names: string[]): boolean {
  const { id } = useCpFormContext();
  return useAppSelector((state) => {
    const dirty = state.form.instances[id]?.dirty || {};

    for (const name of names) {
      if (hasNestedPath(dirty, name)) {
        return true;
      }
    }

    return false;
  }, shallowEqual);
}

export function useCpFormModifiedAnyId(formId: string): boolean {
  return useAppSelector((state) => {
    const dirty = state.form.instances[formId]?.dirty || {};
    const anyDirty = Object.keys(dirty).length > 0;

    return anyDirty;
  }, shallowEqual);
}

export function hasNestedPath(obj: any, path: string) {
  if (typeof obj !== "object" || obj === null) return false;

  const keys = path.split(".");
  let current = obj;

  for (const key of keys) {
    if (current == null) return false;

    if (Array.isArray(current) && /^\d+$/.test(key)) {
      const index = parseInt(key, 10);
      if (index >= current.length) return false;
      current = current[index];
    } else if (typeof current === "object") {
      if (!(key in current)) return false;
      current = current[key];
    } else {
      return false;
    }
  }

  return current !== undefined;
}

export function hasNestedPathWithValue(obj: any, key: string, value: any): boolean {
  if (typeof obj !== "object" || obj === null) return false;

  if (key in obj && obj[key] === value) {
    return true;
  }

  for (const k of Object.keys(obj)) {
    if (hasNestedPathWithValue(obj[k], key, value)) {
      return true;
    }
  }

  return false;
}
